import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { SEOHead } from "../components/SEOHead";
import { Breadcrumbs } from "../components/Breadcrumbs";
import { useFetch } from "../hooks/useFetch";
import { apiEndpoints, API_BASE_URL } from "../utils/api";

export const AboutPage = () => {
  const [misionData, setMisionData] = useState(null);
  const [visionData, setVisionData] = useState(null);
  const [valuesData, setValuesData] = useState([]);
  const [loadingValues, setLoadingValues] = useState(false);
  const [statutesData, setStatutesData] = useState(null);
  const [managersData, setManagersData] = useState([]);
  const [statsData, setStatsData] = useState(null);

  // Cargar configuración del sitio
  const { data: siteSettings } = useFetch(apiEndpoints.settings.public);

  useEffect(() => {
    // Cargar estatutos
    fetch(apiEndpoints.posts.single("estatutos"))
      .then((response) => response.json())
      .then((data) => {
        //console.log('Datos estatutos:', data);
        if (data.success && data.data) {
          setStatutesData(data.data);
        }
      })
      .catch((error) => console.error("Error cargando estatutos:", error));

    // Cargar misión
    fetch(apiEndpoints.posts.single("mision"))
      .then((response) => response.json())
      .then((data) => {
        console.log("Datos misión:", data);
        if (data.success && data.data) {
          setMisionData(data.data);
        }
      })
      .catch((error) => console.error("Error cargando misión:", error));

    // Cargar visión
    fetch(apiEndpoints.posts.single("vision"))
      .then((response) => response.json())
      .then((data) => {
        //console.log('Datos visión:', data);
        if (data.success && data.data) {
          setVisionData(data.data);
        }
      })
      .catch((error) => console.error("Error cargando visión:", error));

    // Cargar valores desde la categoría "nuestros-valores"
    setLoadingValues(true);
    fetch(apiEndpoints.posts.list(1, 20, "nuestros-valores"))
      .then((response) => response.json())
      .then((data) => {
        //console.log('Datos valores:', data);
        if (data.success && data.data && data.data.length > 0) {
          setValuesData(data.data);
        }
        setLoadingValues(false);
      })
      .catch((error) => {
        //console.error('Error cargando valores:', error);
        setLoadingValues(false);
      });

    // Cargar directivos
    fetch(apiEndpoints.managers.list())
      .then((response) => response.json())
      .then((data) => {
        //console.log('Datos directivos:', data);
        if (data.success) {
          setManagersData(data.data);
        }
      })
      .catch((error) => console.error("Error cargando directivos:", error));

    // Cargar estadísticas
    fetch(apiEndpoints.stats.general())
      .then((response) => response.json())
      .then((data) => {
        //console.log('Datos estadísticas:', data);
        if (data.success) {
          setStatsData(data.data);
        }
      })
      .catch((error) => console.error("Error cargando estadísticas:", error));
  }, []);

  const breadcrumbItems = [
    { name: "Inicio", url: "/" },
    { name: "Nosotros", url: "/nosotros" },
  ];

  // Simplificamos - usamos el contenido directo de los posts
  const getMisionContent = () => {
    if (!misionData) return null;
    return {
      title: misionData.title || "Nuestra Misión",
      content:
        misionData.excerpt ||
        misionData.body?.replace(/<[^>]*>/g, "").substring(0, 300) ||
        "",
    };
  };

  const getVisionContent = () => {
    if (!visionData) return null;
    return {
      title: visionData.title || "Nuestra Visión",
      content:
        visionData.excerpt ||
        visionData.body?.replace(/<[^>]*>/g, "").substring(0, 300) ||
        "",
    };
  };

  // Función para extraer contenido de un valor desde su HTML body
  const extractValueFromPost = (post) => {
    if (!post || !post.body) return null;

    try {
      // El HTML viene como: <div class="value-card">...</div>
      const htmlContent = post.body;

      // Extraer el icono (buscar la clase del icono)
      const iconMatch = htmlContent.match(/<i[^>]*class="([^"]*)"[^>]*><\/i>/i);
      const iconClass = iconMatch ? iconMatch[1] : "fas fa-star";

      // Extraer el título (contenido del h4)
      const titleMatch = htmlContent.match(/<h4[^>]*>(.*?)<\/h4>/is);
      const title = titleMatch ? titleMatch[1].trim() : post.title;

      // Extraer la descripción (contenido del p)
      const descMatch = htmlContent.match(/<p[^>]*>(.*?)<\/p>/is);
      const description = descMatch ? descMatch[1].trim() : post.excerpt || "";

      return {
        id: post.id,
        iconClass,
        title,
        description,
        slug: post.slug,
      };
    } catch (error) {
      //console.error('Error procesando valor:', error);
      return {
        id: post.id,
        iconClass: "fas fa-star",
        title: post.title,
        description: post.excerpt || "Descripción no disponible",
        slug: post.slug,
      };
    }
  };

  // Extraer misión y visión del HTML
  const misionContent = getMisionContent();
  const visionContent = getVisionContent();

  return (
    <>
      <SEOHead
        title="Nosotros - Red Arauca"
        description="Conoce la historia, misión, visión y equipo de Red Arauca. Somos una red de medios comprometida con el periodismo de calidad en la región."
        keywords="red arauca, nosotros, historia, equipo, misión, visión, periodismo, medios comunicación"
      />

      <div className="about-page">
        {/* Header con imagen de fondo */}
        <header className="page-header">
          <div className="header-container-left">
            <div className="header-content-left">
              <h1 className="display-4 fw-bold mb-0 mt-4">Nosotros</h1>

              <div className="breadcrumbs-section-header">
                <Breadcrumbs items={breadcrumbItems} />
              </div>

              <div className="header-description">
                <p>
                  Somos una red de medios de comunicación comprometida con la
                  excelencia en el periodismo
                </p>
              </div>
            </div>
          </div>
        </header>

        <div className="container">
          {/* Estatutos Section */}
          {statutesData && (
            <section className="statutes-section">
              <div className="section-header">
                <h2>{statutesData.title}</h2>
                {statutesData.excerpt && <p>{statutesData.excerpt}</p>}
              </div>
              <div className="statutes-content">
                <div dangerouslySetInnerHTML={{ __html: statutesData.body }} />
              </div>
            </section>
          )}

          {/* Mensaje de debug para estatutos */}
          {!statutesData && (
            <div
              style={{
                padding: "20px",
                background: "#f0f0f0",
                margin: "20px 0",
                borderRadius: "8px",
              }}
            >
              <p>
                <strong>Debug:</strong> No se encontraron datos de estatutos.
                Verifica que exista un post con slug "estatutos".
              </p>
            </div>
          )}

          {/* Mission & Vision */}
          {(misionData || visionData) && (
            <section className="mission-vision-section">
              <div className="mission-vision-grid">
                {misionData && (
                  <div className="mission-card">
                    <div className="card-icon">
                      <i className="fas fa-bullseye"></i>
                    </div>
                    <h3>{getMisionContent()?.title}</h3>
                    <p>{getMisionContent()?.content}</p>
                  </div>
                )}

                {visionData && (
                  <div className="vision-card">
                    <div className="card-icon">
                      <i className="fas fa-eye"></i>
                    </div>
                    <h3>{getVisionContent()?.title}</h3>
                    <p>{getVisionContent()?.content}</p>
                  </div>
                )}
              </div>
            </section>
          )}

          {/* Debug para misión y visión */}
          {!misionData && !visionData && (
            <div
              style={{
                padding: "20px",
                background: "#f0f0f0",
                margin: "20px 0",
                borderRadius: "8px",
              }}
            >
              <p>
                <strong>Debug:</strong> No se encontraron posts de misión o
                visión.
              </p>
              <p>Verificar que existan posts con slugs: "mision" y "vision"</p>
            </div>
          )}

          {/* Mensaje de debug para valores */}

          {/* Values Section */}
          {valuesData.length > 0 && (
            <section className="values-section">
              <div className="section-header">
                <h2>Nuestros Valores</h2>
                <p>Los principios que guían nuestro trabajo diario</p>
              </div>

              <div className="values-grid">
                {valuesData.map((post) => (
                  <div
                    key={post.id}
                    dangerouslySetInnerHTML={{ __html: post.body }}
                  />
                ))}
              </div>
            </section>
          )}

          {/* Debug para valores */}
          {valuesData.length === 0 && !loadingValues && (
            <div
              style={{
                padding: "20px",
                background: "#f0f0f0",
                margin: "20px 0",
                borderRadius: "8px",
              }}
            >
              <p>
                <strong>Debug:</strong> No se encontraron posts de valores en la
                categoría "nuestros-valores".
              </p>
              <p>
                Endpoint utilizado:
                /api/posts.php?action=list&page=1&category=nuestros-valores
              </p>
            </div>
          )}

          {loadingValues && (
            <div style={{ padding: "20px", textAlign: "center" }}>
              <p>Cargando valores...</p>
            </div>
          )}

          {/* Team Section */}
          {managersData.length > 0 && (
            <section className="team-section">
              <div className="section-header">
                <h2>Nuestro Equipo Directivo</h2>
                <p>Conoce a las personas que lideran nuestra organización</p>
              </div>

              <div className="team-grid">
                {managersData.map((member) => (
                  <div key={member.id} className="team-card">
                    <div className="member-image">
                      <img
                        src={
                          member.profile_picture_path
                            ? `${API_BASE_URL}/uploads/profiles/${member.profile_picture_path}`
                            : "https://img.freepik.com/vector-gratis/establecimiento-circulos-usuarios_78370-4704.jpg"
                        }
                        alt={member.full_name}
                        loading="lazy"
                      />
                    </div>
                    <div className="member-info">
                      <h4>{member.full_name}</h4>
                      <p className="member-position">
                        {member.executive_title}
                      </p>
                      <p className="member-bio">{member.profiletxt}</p>
                      <div className="member-social">
                        {member.linkedin && (
                          <a
                            href={member.linkedin}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            <i className="fab fa-linkedin-in"></i>
                          </a>
                        )}
                        {member.email && (
                          <a href={`mailto:${member.email}`}>
                            <i className="fas fa-envelope"></i>
                          </a>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </section>
          )}

          {/* Statistics Section */}
          {statsData && (
            <section className="stats-section">
              <div className="section-header">
                <h2>Red Arauca en Números</h2>
                <p>Nuestro impacto en la región</p>
              </div>

              <div className="stats-grid">
                <div className="stat-card">
                  <div className="stat-icon">
                    <i className="fas fa-building"></i>
                  </div>
                  <div className="stat-number">
                    {statsData.media_outlets?.total || 0}+
                  </div>
                  <div className="stat-label">Medios Afiliados</div>
                </div>

                <div className="stat-card">
                  <div className="stat-icon">
                    <i className="fas fa-users"></i>
                  </div>
                  <div className="stat-number">
                    {statsData.social_media?.total_followers
                      ? Math.round(
                          statsData.social_media.total_followers / 1000
                        ) + "K"
                      : "500K"}
                    +
                  </div>
                  <div className="stat-label">Seguidores en Redes</div>
                </div>

                <div className="stat-card">
                  <div className="stat-icon">
                    <i className="fas fa-map-marker-alt"></i>
                  </div>
                  <div className="stat-number">
                    {statsData.cities?.total_cities || 15}
                  </div>
                  <div className="stat-label">Municipios Cubiertos</div>
                </div>

                <div className="stat-card">
                  <div className="stat-icon">
                    <i className="fas fa-newspaper"></i>
                  </div>
                  <div className="stat-number">
                    {statsData.posts?.total || 0}+
                  </div>
                  <div className="stat-label">Artículos Publicados</div>
                </div>
              </div>
            </section>
          )}

          {/* CTA Section */}
          <section className="cta-section">
            <div className="cta-content">
              <h2>¿Quieres ser parte de Red Arauca?</h2>
              <p>
                Si eres un medio de comunicación comprometido con el periodismo
                de calidad y quieres formar parte de nuestra red, nos encantaría
                conocerte.
              </p>
              <div className="cta-buttons">
                <Link to="/contacto" className="cta-button primary">
                  <i className="fas fa-envelope"></i>
                  Contáctenos
                </Link>
                <Link to="/medios" className="cta-button secondary">
                  <i className="fas fa-users"></i>
                  Ver Miembros
                </Link>
              </div>
            </div>
          </section>
        </div>

        <style jsx>{`
          .about-page {
            background: #f8fafc;
            min-height: 100vh;
            padding: 0 0 60px;
          }

          /* Header styles */
          .page-header {
            background: linear-gradient(
                to left,
                transparent 50%,
                rgba(0, 0, 0, 0.6) 100%
              ),
              url("https://img.freepik.com/free-vector/abstract-technology-wire-polygon-background_1035-17448.jpg");
            background-size: cover;
            background-position: center;
            color: white;
            height: 220px;
            position: relative;
            display: flex;
            align-items: center;
            margin: 0;
          }

          .page-header::before {
            content: "";
            position: absolute;
            top: 0;
            left: 0;
            right: 0;
            bottom: 0;
            background: rgba(0, 0, 0, 0.3);
          }

          .page-header .header-container-left {
            position: relative;
            z-index: 2;
            width: 100%;
            padding-left: 40px;
            padding-right: 20px;
          }

          .header-content-left {
            display: flex;
            flex-direction: column;
            align-items: flex-start;
            justify-content: center;
            height: 100%;
            text-align: left;
            max-width: 600px;
          }

          .page-header h1 {
            font-size: 2.5rem;
            font-weight: 800;
            margin: 0;
            text-shadow: 2px 2px 4px rgba(0, 0, 0, 0.5);
            opacity: 0.95;
            line-height: 1.2;
          }

          .breadcrumbs-section-header {
            margin: 16px 0 8px 0;
          }

          .breadcrumbs-section-header nav {
            background: transparent !important;
            border: none !important;
            padding: 0 !important;
          }

          .breadcrumbs-section-header .breadcrumb-container {
            padding: 0 !important;
          }

          .breadcrumbs-section-header .breadcrumb-link {
            color: rgba(255, 255, 255, 0.9) !important;
            text-decoration: underline;
          }

          .breadcrumbs-section-header .breadcrumb-link:hover {
            color: white !important;
          }

          .breadcrumbs-section-header .breadcrumb-current {
            color: rgba(255, 255, 255, 0.8) !important;
            font-weight: 600;
          }

          .breadcrumbs-section-header .breadcrumb-separator {
            color: rgba(255, 255, 255, 0.6) !important;
          }

          .header-description {
            margin-top: 12px;
          }

          .header-description p {
            font-size: 1.1rem;
            color: rgba(255, 255, 255, 0.9);
            margin: 0;
            line-height: 1.5;
          }

          .container {
            max-width: 1200px;
            margin: 0 auto;
            padding: 40px 20px 0;
          }

          .section-header {
            text-align: center;
            margin-bottom: 30px;
          }

          .section-header h2 {
            font-size: 2.5rem;
            font-weight: bold;
            color: #1f2937;
            margin-bottom: 16px;
          }

          .section-header p {
            font-size: 1.2rem;
            color: #6b7280;
            max-width: 600px;
            margin: 0 auto;
          }

          /* Statutes Section */
          .statutes-section {
            margin-bottom: 80px;
          }
          .statutes-content {
            background: white;
            padding: 40px;
            border-radius: 16px;
            box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
          }
          .estatutos-card {
            background: white;
            padding: 40px;
            border-radius: 16px;
            display: flex; /* Usamos Flexbox para alinear el contenido y el icono */
            align-items: center;
            justify-content: space-between; /* Empuja el contenido y el icono a los extremos */
          }

          /* Estilos para el contenido de texto */
          .card-content {
            text-align: left;
            flex-grow: 1;
            padding-right: 20px; /* Espacio entre el texto y el icono */
          }

          /* Estilos para el ícono, se mantiene la base pero sin margen automático */
          .estatutos-icon {
            width: 80px;
            height: 80px;
            background: linear-gradient(135deg, #10b981, #3b82f6);
            border-radius: 50%;
            display: flex;
            align-items: center;
            justify-content: center;
            color: white;
            font-size: 2rem;
            flex-shrink: 0;
          }

          /* Estilos para el título y el párrafo */
          .estatutos-card h3 {
            font-size: 1.8rem;
            font-weight: bold;
            color: #1f2937;
            margin-bottom: 20px;
          }

          .estatutos-card p {
            font-size: 1.1rem;
            color: #6b7280;
            line-height: 1.7;
            margin-bottom: 20px;
          }

          /* Estilos para el enlace de descarga */
          .download-link {
            display: inline-block;
            padding: 10px 20px;
            background: #3b82f6;
            color: white;
            border-radius: 8px;
            text-decoration: none;
            font-weight: bold;
            transition: background 0.3s ease;
          }

          .download-link:hover {
            background: #2563eb;
          }

          /* Mission & Vision */
          .mission-vision-section {
            margin-bottom: 80px;
          }

          .mission-vision-grid {
            display: grid;
            grid-template-columns: 1fr 1fr;
            gap: 30px;
          }

          .mission-card,
          .vision-card {
            background: white;
            padding: 40px;
            border-radius: 16px;
            box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
            text-align: center;
          }

          .card-icon {
            width: 80px;
            height: 80px;
            background: #3b82f6;
            border-radius: 50%;
            display: flex;
            align-items: center;
            justify-content: center;
            margin: 0 auto 25px;
            color: white;
            font-size: 2rem;
          }

          .mission-card h3,
          .vision-card h3 {
            font-size: 1.8rem;
            font-weight: bold;
            color: #1f2937;
            margin-bottom: 20px;
          }

          .mission-card p,
          .vision-card p {
            font-size: 1.1rem;
            color: #6b7280;
            line-height: 1.7;
          }

          /* Values Section */
          .values-section {
            margin-bottom: 80px;
          }

          .values-grid {
            display: grid;
            grid-template-columns: repeat(auto-fit, minmax(350px, 1fr));
            gap: 30px;
          }

          .value-card {
            background: white;
            padding: 30px;
            border-radius: 12px;
            box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
            text-align: center;
            transition: transform 0.2s ease;
          }

          .value-card:hover {
            transform: translateY(-4px);
          }

          .value-icon {
            width: 70px;
            height: 70px;
            background: linear-gradient(135deg, #3b82f6, #10b981);
            border-radius: 50%;
            display: flex;
            align-items: center;
            justify-content: center;
            margin: 0 auto 20px;
            color: white;
            font-size: 1.5rem;
          }

          .value-card h4 {
            font-size: 1.3rem;
            font-weight: bold;
            color: #1f2937;
            margin-bottom: 15px;
          }

          .value-card p {
            color: #6b7280;
            line-height: 1.6;
          }
          /* Team Section */
          .team-section {
            margin-bottom: 80px;
          }

          .team-grid {
            display: grid;
            grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
            gap: 30px;
          }

          .team-card {
            background: white;
            border-radius: 16px;
            padding: 30px;
            box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
            text-align: center;
            transition: transform 0.2s ease;
          }

          .team-card:hover {
            transform: translateY(-4px);
          }

          .member-image {
            width: 150px;
            height: 150px;
            margin: 0 auto 20px;
            border-radius: 50%;
            overflow: hidden;
            border: 4px solid #f3f4f6;
          }

          .member-image img {
            width: 100%;
            height: 100%;
            object-fit: cover;
          }

          .member-info h4 {
            font-size: 1.3rem;
            font-weight: bold;
            color: #1f2937;
            margin-bottom: 8px;
          }

          .member-position {
            color: #3b82f6;
            font-weight: 600;
            margin-bottom: 15px;
            font-size: 1rem;
          }

          .member-bio {
            color: #6b7280;
            line-height: 1.6;
            margin-bottom: 20px;
            font-size: 0.95rem;
          }

          .member-social {
            display: flex;
            justify-content: center;
            gap: 12px;
          }

          .member-social a {
            width: 40px;
            height: 40px;
            background: #f3f4f6;
            border-radius: 50%;
            display: flex;
            align-items: center;
            justify-content: center;
            color: #6b7280;
            text-decoration: none;
            transition: all 0.2s ease;
          }

          .member-social a:hover {
            background: #3b82f6;
            color: white;
          }

          /* Statistics Section */
          .stats-section {
            margin-bottom: 80px;
          }

          .stats-grid {
            display: grid;
            grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
            gap: 30px;
          }

          .stat-card {
            background: white;
            padding: 40px 30px;
            border-radius: 16px;
            box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
            text-align: center;
          }

          .stat-icon {
            width: 70px;
            height: 70px;
            background: linear-gradient(135deg, #10b981, #3b82f6);
            border-radius: 50%;
            display: flex;
            align-items: center;
            justify-content: center;
            margin: 0 auto 20px;
            color: white;
            font-size: 1.8rem;
          }

          .stat-number {
            font-size: 3rem;
            font-weight: bold;
            color: #1f2937;
            line-height: 1;
            margin-bottom: 8px;
          }

          .stat-label {
            color: #6b7280;
            font-weight: 500;
            font-size: 1.1rem;
          }

          /* CTA Section */
          .cta-section {
            background: linear-gradient(135deg, #3b82f6, #10b981);
            border-radius: 16px;
            padding: 60px 40px;
            text-align: center;
            color: white;
          }

          .cta-content h2 {
            font-size: 2.5rem;
            font-weight: bold;
            margin-bottom: 20px;
          }

          .cta-content p {
            font-size: 1.2rem;
            margin-bottom: 30px;
            opacity: 0.9;
            max-width: 600px;
            margin-left: auto;
            margin-right: auto;
          }

          .cta-buttons {
            display: flex;
            gap: 20px;
            justify-content: center;
            flex-wrap: wrap;
          }

          .cta-button {
            display: flex;
            align-items: center;
            gap: 8px;
            padding: 16px 32px;
            border-radius: 8px;
            font-weight: 600;
            text-decoration: none;
            transition: all 0.2s ease;
            font-size: 1.1rem;
          }

          .cta-button.primary {
            background: white;
            color: #3b82f6;
          }

          .cta-button.primary:hover {
            background: #f9fafb;
            transform: translateY(-2px);
            color: #3b82f6;
          }

          .cta-button.secondary {
            background: transparent;
            color: white;
            border: 2px solid white;
          }

          .cta-button.secondary:hover {
            background: white;
            color: #3b82f6;
          }

          @media (max-width: 768px) {
            .page-header {
              height: 180px;
            }

            .page-header .header-container-left {
              padding-left: 20px;
              padding-right: 16px;
            }

            .page-header h1 {
              font-size: 1.8rem;
            }

            .header-description p {
              font-size: 1rem;
            }

            .container {
              padding: 24px 16px 0;
            }

            .section-header h2 {
              font-size: 2rem;
            }

            .mission-vision-grid {
              grid-template-columns: 1fr;
            }

            .values-grid {
              grid-template-columns: 1fr;
            }

            .team-grid {
              grid-template-columns: 1fr;
            }

            .stats-grid {
              grid-template-columns: repeat(2, 1fr);
            }

            .cta-section {
              padding: 40px 24px;
            }

            .cta-content h2 {
              font-size: 2rem;
            }

            .cta-buttons {
              flex-direction: column;
              align-items: center;
            }

            .cta-button {
              width: 100%;
              max-width: 300px;
              justify-content: center;
            }

            .breadcrumbs-section-header .breadcrumb-list {
              font-size: 0.8rem;
            }
          }
        `}</style>
      </div>
    </>
  );
};
